import React, { forwardRef } from "react";
import { View, StyleSheet } from "react-native";
import TextareaAutosize from "react-textarea-autosize";
import { useDevice } from "../_layout";
import { normalizeMobileScaledTextareaProps } from "./mobileScaledTextareaAccessibility";

/**
 * MobileScaledTextarea - A wrapper around TextareaAutosize that prevents
 * iOS Safari auto-zoom on focus by using 16px font with transform scaling.
 *
 * On desktop or non-iOS mobile: Renders normally with the specified fontSize.
 * On iOS mobile: Uses 16px font and scales down to match the visual fontSize.
 *
 * iOS Safari auto-zooms on input focus when font-size < 16px. Using transform: scale()
 * keeps the CSS font-size at 16px (which Safari reads) while visually displaying smaller text.
 *
 * IMPROVED CURSOR POSITIONING:
 * - Uses GPU acceleration (translateZ(0)) for better rendering
 * - Better line-height calculation to match visual size
 * - Improved overflow handling
 * - For fontSize >= 14px, uses 16px directly (no scaling) for perfect cursor accuracy
 *
 * Props:
 * - fontSize: The visual font size (default 12)
 * - style: Style object for the textarea (will be merged)
 * - className: CSS class name for the textarea
 * - containerWidth: Optional fixed container width (for iOS transform scaling calculation)
 * - useDirect16px: If true, use 16px directly when fontSize >= 14px (better cursor, default: true)
 * - All other props are passed to TextareaAutosize
 */
const BASE_FONT_SIZE = 16;
const MIN_FONT_SIZE_FOR_DIRECT = 14; // Use 16px directly if fontSize >= this (better cursor)
const DEFAULT_FONT_SIZE = 12;
const MIN_SCALE = 0.01;

const MobileScaledTextarea = forwardRef(
  (
    {
      fontSize = 12,
      style = {},
      className,
      containerWidth,
      useDirect16px = true,
      ...rest
    },
    ref,
  ) => {
    const { isMobile, isIOS } = useDevice();
    const safeFontSize =
      typeof fontSize === "number" && Number.isFinite(fontSize) && fontSize > 0
        ? fontSize
        : DEFAULT_FONT_SIZE;
    const normalizedTextareaProps = normalizeMobileScaledTextareaProps(rest);

    // Combine className to hide scrollbar by default
    const combinedClassName = `${className || ""} mobile-scaled-textarea-no-scrollbar`.trim();

    // Scrollbar hiding styles applied to all instances
    const scrollbarHidingStyles = {
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE/Edge
    };

    // DESKTOP or non-iOS mobile: Render normally without scaling
    if (!isMobile || !isIOS) {
      return (
        <TextareaAutosize
          ref={ref}
          className={combinedClassName}
          style={{
            ...style,
            fontSize: safeFontSize,
            ...scrollbarHidingStyles,
          }}
          {...normalizedTextareaProps}
        />
      );
    }

    // iOS MOBILE: Strategy selection based on fontSize
    // For fontSize >= 14px: Use 16px directly (perfect cursor, minimal visual difference)
    // For fontSize < 14px: Use transform scale (smaller visual size, slight cursor offset)
    if (useDirect16px && safeFontSize >= MIN_FONT_SIZE_FOR_DIRECT) {
      // Use 16px directly - no scaling needed, perfect cursor positioning
      return (
        <TextareaAutosize
          ref={ref}
          className={combinedClassName}
          style={{
            ...style,
            fontSize: BASE_FONT_SIZE, // 16px prevents zoom, close enough to visual size
            ...scrollbarHidingStyles,
          }}
          {...normalizedTextareaProps}
        />
      );
    }

    // iOS MOBILE: Use transform: scale() for smaller font sizes
    // Safari checks the CSS font-size value (16px) and doesn't auto-zoom
    // Improved with GPU acceleration and better line-height
    const textScale = Math.max(safeFontSize / BASE_FONT_SIZE, MIN_SCALE);
    const scaleCompensation = 1 / textScale;

    // Preserve string values (e.g. "normal") and only scale numeric line-height values.
    const lineHeightFromStyle = style?.lineHeight;
    let scaledLineHeight = BASE_FONT_SIZE * 1.2;
    if (
      typeof lineHeightFromStyle === "number" &&
      Number.isFinite(lineHeightFromStyle)
    ) {
      scaledLineHeight = lineHeightFromStyle / textScale;
    } else if (
      typeof lineHeightFromStyle === "string" &&
      lineHeightFromStyle.trim() !== ""
    ) {
      scaledLineHeight = lineHeightFromStyle;
    }

    return (
      <View style={styles.mobileContainerIOS}>
        <TextareaAutosize
          ref={ref}
          className={combinedClassName}
          style={{
            ...style,
            fontSize: BASE_FONT_SIZE,
            lineHeight: scaledLineHeight,
            width: containerWidth
              ? containerWidth * scaleCompensation
              : `${100 * scaleCompensation}%`,
            transform: `scale(${textScale}) translateZ(0)`, // translateZ(0) forces GPU rendering
            transformOrigin: "center left",
            willChange: "transform", // Hint to browser for better performance
            boxSizing: "border-box",
            padding: 0,
            margin: 0,
            // Prevent text selection issues with scaled text
            WebkitUserSelect: "text",
            userSelect: "text",
            ...scrollbarHidingStyles,
            // marginBottom: -3,
          }}
          {...normalizedTextareaProps}
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  mobileContainerIOS: {
    width: "100%",
    // Don't clip overflow - allows descenders to show properly with transform
    overflow: "visible",
    // Ensure proper stacking context for cursor rendering
    position: "relative",
  },
});

export default MobileScaledTextarea;
